import { ReactNode } from 'react'

import { Steps } from 'antd'
import classNames from 'classnames'

import { WizardStep, StepStatus as WizardStepStatus } from 'core/domain/wizard/wizard'

import Progress from 'common/components/feedback/Progress'

import { Col, Row, Skeleton } from 'common/components'

import { NavItemProps } from './models'

const { Step } = Steps
const DEFAULT_NAVIGATION_ITEM_INDEX = 0

declare type StepStatus = 'finish' | 'process' | 'wait'

export interface EditingProps {
  index: number
  key: string | undefined
}

interface NavigationProps {
  navigationItems: WizardStep[]
  hasDirtyData: boolean
  setCurrentNavigationItem: (navigationItem: NavItemProps) => void
  setOnNavigationConfirmVisible: (state: boolean) => void
  setTemporaryState: (editing: string) => void
  bindGoBack: (goBack: () => void) => void
  bindGoNext: (goNext: () => void) => void
  bindRedirectTo: (redirectTo: (id: string) => void) => void
  bindGetIndexOfActiveNavigationItem: (getIndexOfActiveNavigationItem: (items: WizardStep[]) => number) => void
  editingNavigationItem: string | undefined
  setEditingNavigationItem: (item: string | undefined) => void
  translate: any
  navigationMapping: NavItemProps[]
  wizardTitle?: string
}

const getPercentageOfCompletedItems = (items: WizardStep[]): number => {
  const numberOfCompletedItems = items.reduce(
    (total, current) => total + (current.status === WizardStepStatus.Complete ? 1 : 0),
    0,
  )
  return Math.round((numberOfCompletedItems * 100) / items.length)
}

const SidebarNavigation = (props: NavigationProps) => {
  const {
    bindGoBack,
    bindGoNext,
    bindRedirectTo,
    bindGetIndexOfActiveNavigationItem,
    setEditingNavigationItem,
    translate,
    wizardTitle = translate('admin-dashboard.benefit-details.class-design.product-class-design-title'),
  } = props
  const editingNavigationItem = props.editingNavigationItem

  const navigationItemsGenerator = (items: WizardStep[]): ReactNode => {
    const activeNavigationItemIndex = getIndexOfActiveNavigationItem(items)
    return (
      <Steps current={activeNavigationItemIndex} direction="vertical" size="small">
        {items.map((item, index) => {
          const categoryItemStatus = getNavigationItemStatus(item)
          return (
            <Step
              title={getTitleContentOfNavigationItem(item)}
              key={index}
              status={categoryItemStatus}
              className={classNames({
                steps__item: true,
                steps__item_active: activeNavigationItemIndex === index,
                steps__item_complete: categoryItemStatus === 'finish',
                steps__item_disabled: categoryItemStatus === 'wait',
              })}
              disabled={categoryItemStatus === 'wait'}
            />
          )
        })}
      </Steps>
    )
  }

  const getIndexOfActiveNavigationItem = (items: WizardStep[]): number => {
    try {
      if (editingNavigationItem) {
        const navItemIndex = items.findIndex((item) => item.id === editingNavigationItem)
        if (navItemIndex === -1) throw new Error('Navigation Item not found')
        const navItem = items[navItemIndex]
        if (navItem.status === WizardStepStatus.Active || navItem.status === WizardStepStatus.Complete)
          return navItemIndex
        else setEditingNavigationItem(undefined)
      }

      const activeNavigationItemIndex = items.findIndex((item) => item.status === WizardStepStatus.Active)

      if (activeNavigationItemIndex === -1) return DEFAULT_NAVIGATION_ITEM_INDEX
      return activeNavigationItemIndex
    } catch (e) {
      console.log(items)
      console.log(e)
      return DEFAULT_NAVIGATION_ITEM_INDEX
    }
  }

  const getNavigationItemStatus = (navigationItem: WizardStep): StepStatus => {
    if (editingNavigationItem === navigationItem.id) {
      return 'process'
    } else if (editingNavigationItem && navigationItem.status === WizardStepStatus.Active) {
      return 'wait'
    } else if (navigationItem.status === WizardStepStatus.Complete) {
      return 'finish'
    } else if (navigationItem.status === WizardStepStatus.Active) {
      return 'process'
    } else {
      return 'wait'
    }
  }

  const getTitleContentOfNavigationItem = (navigationItem: WizardStep): ReactNode => {
    return navigationItem.status === WizardStepStatus.Active || navigationItem.status === WizardStepStatus.Complete ? (
      <label
        className="pointer"
        onClick={() => {
          if (props.hasDirtyData) {
            props.setOnNavigationConfirmVisible(true)
            props.setTemporaryState(navigationItem.id)
          } else {
            redirectToNavigationItem(navigationItem.id)
          }
        }}
      >
        {navigationItem.name}
      </label>
    ) : (
      navigationItem.name
    )
  }

  const redirectToNavigationItem = (id: string) => {
    try {
      const navigationItemProps = props.navigationMapping.find((navItem) => navItem.key === id)
      if (navigationItemProps === undefined) throw new Error('Navigation Item Props not found')
      props.setCurrentNavigationItem(navigationItemProps)
      setEditingNavigationItem(id)
    } catch (e) {
      console.log(id)
      console.log(e)
    }
  }

  const goNext = () => {
    try {
      const currentNavigationItemIndex = getIndexOfActiveNavigationItem(props.navigationItems)
      const item = props.navigationItems[currentNavigationItemIndex]
      if (item === undefined) throw new Error('Navigation item not found')
      item.status = WizardStepStatus.Complete
      if (currentNavigationItemIndex < props.navigationItems.length - 1) {
        let nextItem = props.navigationItems[currentNavigationItemIndex + 1]
        if (nextItem.status === WizardStepStatus.Disabled) {
          nextItem.status = WizardStepStatus.Active
        }
        redirectToNavigationItem(nextItem.id)
      } else {
        redirectToNavigationItem(props.navigationItems[DEFAULT_NAVIGATION_ITEM_INDEX].id)
      }
    } catch (e) {
      console.log(e)
      redirectToNavigationItem(props.navigationItems[DEFAULT_NAVIGATION_ITEM_INDEX].id)
    }
  }

  const goBack = () => {
    try {
      const currentNavigationItemIndex = getIndexOfActiveNavigationItem(props.navigationItems)
      const item = props.navigationItems[currentNavigationItemIndex]
      if (item === undefined) throw new Error('Navigation item not found')

      if (currentNavigationItemIndex > 0) {
        let nextItem = props.navigationItems[currentNavigationItemIndex - 1]
        redirectToNavigationItem(nextItem.id)
      } else {
        redirectToNavigationItem(props.navigationItems[DEFAULT_NAVIGATION_ITEM_INDEX].id)
      }
    } catch (e) {
      console.log(e)
      redirectToNavigationItem(props.navigationItems[DEFAULT_NAVIGATION_ITEM_INDEX].id)
    }
  }

  bindGetIndexOfActiveNavigationItem(getIndexOfActiveNavigationItem)
  bindGoNext(goNext)
  bindGoBack(goBack)
  bindRedirectTo(redirectToNavigationItem)

  return (
    <Skeleton
      loading={props.navigationItems.length === 0}
      active
      paragraph={{
        rows: 10,
      }}
    >
      {props.navigationItems ? (
        <section className="product_class_navigation">
          <Row>
            <Col className="progress">
              <h5 className="title">{wizardTitle}</h5>
              <Progress
                size="small"
                type="line"
                width={161}
                showInfo={!(getPercentageOfCompletedItems(props.navigationItems) === 0)}
                percent={getPercentageOfCompletedItems(props.navigationItems)}
                strokeColor="#11d175"
              />
            </Col>
          </Row>
          <Row>
            <Col span={24} className="menu">
              {navigationItemsGenerator(props.navigationItems)}
            </Col>
          </Row>
        </section>
      ) : null}
    </Skeleton>
  )
}

export default SidebarNavigation
